import api from '../axios.config';
import { API_ENDPOINTS, createEndpoint } from './endpoints';

export const ResumeService = {
  sendOtp: async (data) => {
    const response = await api.post(API_ENDPOINTS.RESUME.SIGNUP_SENDOTP, data);
    return response;
  },

  verifyOtp: async (data) => {
    const response = await api.post(
      API_ENDPOINTS.RESUME.SIGNUP_VERIFYOTP,
      data
    );
    return response;
  },
  resendOtp: async (data) => {
    const response = await api.post(
      API_ENDPOINTS.RESUME.SIGNUP_RESENDOTP,
      data
    );
    return response;
  },
  getSignedUrl: async (fileData) => {
    const response = await api.post(
      API_ENDPOINTS.RESUME.REQUESTSIGNEDURL,
      fileData
    );
    return response;
  },

  submitForm: async (formData) => {
    const response = await api.post(
      API_ENDPOINTS.RESUME.FORMSUBMISSION,
      formData
    );

    return response;
  },

  getProfile: async (candidateId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.GETPROFILE, {
      id: candidateId,
    });
    const response = await api.get(endpoint);

    // New API response structure: { success, message, data: { candidate_id, profile_data } }
    // Return the inner data object
    return response.data?.data || response.data;
  },

  updateProfile: async (candidateId, data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.UPDATEPROFILE, {
      id: candidateId,
    });

    const response = await api.put(endpoint, data);
    return response;
  },

  getResumeProfile: async (resumeToken) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.RESUMEPROFILE, {
      token: resumeToken,
    });
    const response = await api.get(endpoint);

    // New API response structure: { success, message, data: { candidate_id, profile_data } }
    // Return the inner data object if it exists, otherwise return response.data for backward compatibility
    return response.data?.data || response.data || response;
  },
  updateProfilePublic: async (candidateId, data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.UPDATEPROFILE_PUBLIC, {
      id: candidateId,
    });
    const response = await api.patch(endpoint, data);
    return response;
  },
  getIsProcessed: async (candidateId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.GETISPROCESSED, {
      id: candidateId,
    });
    const response = await api.get(endpoint);

    return response;
  },
  getSummary: async (candidateId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.GETSUMMARY, {
      id: candidateId,
    });
    const response = await api.get(endpoint);

    return response.data;
  },

  updateProfileSection: async (candidateId, data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.UPDATEPROFILE_SECTION,
      {
        id: candidateId,
      }
    );

    const response = await api.patch(endpoint, data);
    return response;
  },

  enhanceWorkExperienceResponsibility: async (description) => {
    const response = await api.post(
      API_ENDPOINTS.RESUME.ENHANCE_WORK_EXPERIENCE_RESPONSIBILITY,
      { description }
    );
    return response;
  },

  // ── Work-experience CRUD ──────────────────────────────────────────────────
  /**
   * Create + edit work experiences in one request.
   * body: { work_experiences: [ {company_name, role, from_date, to_date,
   *   is_current, duties:[...] }  // new (no id) — duties embedded
   *   | { id, ...changedFields }  // existing — fields only, no duties ] }
   */
  saveWorkExperiences: async (workExperiences) => {
    const response = await api.post(API_ENDPOINTS.RESUME.WORK_EXPERIENCE, {
      work_experiences: workExperiences,
    });
    return response;
  },

  deleteWorkExperience: async (experienceId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.WORK_EXPERIENCE_BY_ID,
      {
        id: experienceId,
      }
    );
    const response = await api.delete(endpoint);
    return response;
  },

  /**
   * Fetch the duties of a work experience by its id.
   * GET /candidates/profile/work-experience/:expId/duties
   * Used to reconcile duty ids after an add (the add response carries no id).
   * Returns the duties array (tolerant of common response wrappers).
   */
  getWorkExperienceDuties: async (experienceId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.WORK_EXPERIENCE_DUTIES,
      { expId: experienceId }
    );
    const response = await api.get(endpoint);
    const root = response?.data?.data ?? response?.data ?? response ?? {};
    if (Array.isArray(root)) return root;
    const duties =
      root.duties ??
      root.work_experience?.duties ??
      root.workExperience?.duties ??
      [];
    return Array.isArray(duties) ? duties : [];
  },

  // ── Duty CRUD (nested under an existing work experience) ───────────────────
  addWorkExperienceDuty: async (experienceId, duty) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.WORK_EXPERIENCE_DUTIES,
      { expId: experienceId }
    );
    const response = await api.post(endpoint, duty);
    return response;
  },

  updateWorkExperienceDuty: async (experienceId, dutyId, duty) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.WORK_EXPERIENCE_DUTY_BY_ID,
      { expId: experienceId, dutyId }
    );
    const response = await api.put(endpoint, duty);
    return response;
  },

  deleteWorkExperienceDuty: async (experienceId, dutyId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.WORK_EXPERIENCE_DUTY_BY_ID,
      { expId: experienceId, dutyId }
    );
    const response = await api.delete(endpoint);
    return response;
  },

  /**
   * Resolve city + state from a 6-digit PIN code.
   * GET /candidates/pincode/:pincode
   * Returns { city_id, city_name, state_id, state_name } (tolerant of wrappers).
   */
  getLocationByPincode: async (pincode) => {
    const endpoint = createEndpoint(API_ENDPOINTS.RESUME.PINCODE_LOOKUP, {
      pincode,
    });
    const response = await api.get(endpoint);
    const body = response?.data?.data ?? response?.data ?? response ?? {};
    return {
      city_id: body.city_id ?? null,
      city_name: body.city_name ?? '',
      state_id: body.state_id ?? null,
      state_name: body.state_name ?? '',
    };
  },

  /**
   * Fetch reference data, optionally scoped with `include` / `exclude` keys so a
   * page/component only pulls the lists it needs. Keys are sent as *repeated*
   * query params (e.g. `?exclude=work_mode&exclude=states`), not comma-joined.
   */
  /**
   * Fetch the server-generated resume PDF as a Blob.
   * GET /candidates/profile/resume-pdf (Content-Type: application/pdf)
   * Uses a longer timeout than the default 10s — the server render is heavy.
   */
  getResumePdf: async () => {
    // Response interceptor returns response.data, which for responseType:'blob'
    // is the PDF Blob itself.
    const blob = await api.get(API_ENDPOINTS.RESUME.RESUME_PDF, {
      responseType: 'blob',
      timeout: 30000, // 10s default + 20s for this endpoint
    });
    return blob;
  },

  getReferenceData: async ({ include, exclude } = {}) => {
    const params = new URLSearchParams();
    (Array.isArray(include) ? include : []).forEach((k) =>
      params.append('include', k)
    );
    (Array.isArray(exclude) ? exclude : []).forEach((k) =>
      params.append('exclude', k)
    );
    const qs = params.toString();
    const endpoint = qs
      ? `${API_ENDPOINTS.RESUME.GETREFERENCEDATA}?${qs}`
      : API_ENDPOINTS.RESUME.GETREFERENCEDATA;
    const response = await api.get(endpoint);
    // New API response structure: { success, message, data: {...} }
    // Return the inner data object if it exists, otherwise return response.data for backward compatibility
    return response.data?.data || response.data;
  },

  /**
   * Request OTP to verify a new email address for profile email change.
   * POST /ncandidates/profile/email/request-otp
   */
  requestEmailChangeOTP: async (email) => {
    const response = await api.post(API_ENDPOINTS.PROFILE_EMAIL.REQUEST_OTP, {
      email,
    });
    return response;
  },

  /**
   * Resend OTP for profile email change.
   * POST /ncandidates/profile/email/resend-otp
   */
  resendEmailChangeOTP: async (email) => {
    const response = await api.post(API_ENDPOINTS.PROFILE_EMAIL.RESEND_OTP, {
      email,
    });
    return response;
  },

  /**
   * Verify the OTP and complete the email change.
   * POST /ncandidates/profile/email/verify-change
   */
  verifyEmailChange: async (email, otp) => {
    const response = await api.post(API_ENDPOINTS.PROFILE_EMAIL.VERIFY_CHANGE, {
      email,
      otp,
    });
    return response;
  },

  /**
   * Get upload processing status for a candidate.
   * GET /candidates/:id/profile/upload-processing
   * New shape: data: { status, step?, total_steps?, percent?, label? }
   * Legacy: data: { upload_processing: boolean }
   */
  getUploadProcessingStatus: async (candidateId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.GET_UPLOAD_PROCESSING,
      {
        id: candidateId,
      }
    );
    const response = await api.get(endpoint);
    const raw =
      response.data?.data !== undefined ? response.data.data : response.data;

    if (
      raw &&
      typeof raw === 'object' &&
      raw.status != null &&
      typeof raw.status === 'string'
    ) {
      return {
        status: raw.status,
        step: typeof raw.step === 'number' ? raw.step : undefined,
        total_steps: typeof raw.total_steps === 'number' ? raw.total_steps : 4,
        percent: typeof raw.percent === 'number' ? raw.percent : undefined,
        label: typeof raw.label === 'string' ? raw.label : undefined,
      };
    }

    const legacyBusy = Boolean(
      raw?.upload_processing ??
      response.data?.data?.upload_processing ??
      response.data?.upload_processing
    );
    if (legacyBusy) {
      return {
        status: 'processing',
        step: undefined,
        total_steps: 4,
        percent: undefined,
        label: undefined,
      };
    }
    return {
      status: 'awaiting_upload',
      step: undefined,
      total_steps: 4,
      percent: undefined,
      label: undefined,
    };
  },

  /**
   * Get reference data for public profile-link (with token). Supports the same
   * repeated-key `include` / `exclude` scoping as getReferenceData.
   * GET /profile-link/reference-data?token={token}
   */
  getReferenceDataPublic: async (token, { include, exclude } = {}) => {
    const params = new URLSearchParams();
    params.append('token', token);
    (Array.isArray(include) ? include : []).forEach((k) =>
      params.append('include', k)
    );
    (Array.isArray(exclude) ? exclude : []).forEach((k) =>
      params.append('exclude', k)
    );
    const endpoint = `${API_ENDPOINTS.RESUME.GETREFERENCEDATA_PUBLIC}?${params.toString()}`;
    const response = await api.get(endpoint);
    return response.data?.data || response.data;
  },

  /**
   * Get profile by token for public profile-link
   * GET /candidates/profile-link?token={token}
   */
  getProfilePublic: async (token) => {
    const endpoint = `${API_ENDPOINTS.RESUME.GETPROFILE_PUBLIC}?token=${encodeURIComponent(token)}`;
    const response = await api.get(endpoint);
    return response.data?.data || response.data;
  },

  /**
   * Update profile section for public profile-link
   * PATCH /candidates/:id/profile-link/
   */
  updateProfileSectionPublic: async (candidateId, data, token) => {
    console.log('[updateProfileSectionPublic] Starting PATCH request:', {
      candidateId,
      token: token ? `${token.substring(0, 20)}...` : 'missing',
      endpointTemplate: API_ENDPOINTS.RESUME.UPDATEPROFILE_PUBLIC_LINK,
      apiBaseURL: api.defaults.baseURL,
    });

    const endpoint = createEndpoint(
      API_ENDPOINTS.RESUME.UPDATEPROFILE_PUBLIC_LINK,
      {
        id: candidateId,
      }
    );
    console.log(
      '[updateProfileSectionPublic] Endpoint after createEndpoint:',
      endpoint
    );

    // Add token as query parameter
    const endpointWithToken = `${endpoint}?token=${encodeURIComponent(token)}`;
    console.log(
      '[updateProfileSectionPublic] Final endpoint with token:',
      endpointWithToken
    );
    console.log(
      '[updateProfileSectionPublic] API instance baseURL:',
      api.defaults.baseURL
    );
    console.log(
      '[updateProfileSectionPublic] Full URL will be:',
      `${api.defaults.baseURL}${endpointWithToken}`
    );

    const response = await api.patch(endpointWithToken, data);
    return response;
  },
};
